import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, Heart, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EncuestaPublica = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [notas, setNotas] = useState([]);
  const [comentario, setComentario] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/encuesta-publica/${token}`);
        setData(res.data);
        const preguntas = res.data?.preguntas || [];
        setNotas(preguntas.map(() => 0));
      } catch (e) {
        setData({ error: e.response?.data?.detail || 'Link inválido' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async () => {
    if (notas.some(n => n === 0)) {
      toast.error('Por favor califica todas las preguntas (1 a 5 estrellas)');
      return;
    }
    setEnviando(true);
    try {
      await axios.post(`${BACKEND}/api/encuesta-publica/${token}/responder`, {
        notas,
        comentario
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error enviando respuesta');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-orange-50 flex items-center justify-center">
        <p className="text-slate-600">Cargando...</p>
      </div>
    );
  }

  if (data?.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-orange-50 flex items-center justify-center p-6">
        <Card className="max-w-md p-8 text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Link no válido</h2>
          <p className="text-sm text-slate-600">{data.error}</p>
        </Card>
      </div>
    );
  }

  if (data?.ya_respondida || submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-orange-50 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="max-w-md p-10 text-center border-emerald-200">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Gracias por tu respuesta!</h2>
            <p className="text-sm text-slate-600">Tu opinión es muy valiosa para Peritum. La incorporaremos para mejorar nuestros servicios.</p>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-orange-50 py-10 px-4" data-testid="encuesta-publica-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-8 h-8 text-rose-500" strokeWidth={1.5} />
            <h1 className="text-3xl font-bold text-slate-900">Peritum</h1>
          </div>
          <p className="text-sm text-slate-600">Encuesta de Satisfacción</p>
        </div>

        <Card className="p-8 bg-white shadow-lg">
          <p className="text-lg text-slate-800 mb-1">Hola <strong>{data.cliente_nombre}</strong>,</p>
          {data.campana_cuerpo && (
            <p className="text-sm text-slate-600 whitespace-pre-line mb-6">{data.campana_cuerpo}</p>
          )}

          <div className="space-y-6">
            {(data.preguntas || []).map((pregunta, idx) => (
              <StarRating
                key={idx}
                label={pregunta}
                value={notas[idx] || 0}
                onChange={(v) => {
                  const next = [...notas];
                  next[idx] = v;
                  setNotas(next);
                }}
                testId={`star-p${idx + 1}`}
              />
            ))}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                ¿Algún comentario o sugerencia? <span className="text-xs text-slate-400">(opcional)</span>
              </label>
              <Textarea
                rows={4}
                placeholder="Cuéntanos qué podemos mejorar..."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                data-testid="comentario-input"
              />
            </div>

            <Button
              className="w-full bg-rose-500 hover:bg-rose-600"
              onClick={handleSubmit}
              disabled={enviando}
              data-testid="submit-encuesta-btn"
            >
              <Send className="w-4 h-4 mr-2" /> {enviando ? 'Enviando...' : 'Enviar respuesta'}
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

const StarRating = ({ label, value, onChange, testId }) => {
  const [hover, setHover] = useState(0);
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-2 block">{label}</label>
      <div className="flex items-center gap-1" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={`w-9 h-9 ${(hover || value) >= i ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
            />
          </button>
        ))}
        {value > 0 && <span className="ml-2 text-sm font-medium text-slate-600">{value}/5</span>}
      </div>
    </div>
  );
};

export default EncuestaPublica;
